import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { fetchData } from '../../services/dataApi';
import LanguageFlag from '../../components/LanguageFlag';

export default function QuickSheetPage() {
  const { t } = useTranslation();
  const { languagePrefs, openLanguagePreferences } = useAuth();
  const { languages } = useLanguages();
  const targetCode = languagePrefs?.targetLanguage || 'en';
  const target = useMemo(() => languages.find((item) => item.code === targetCode), [languages, targetCode]);
  const [sheet, setSheet] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setSheet(null);
    setError(false);
    fetchData(`quick-sheets/${targetCode}.json`)
      .then((data) => active && setSheet(data))
      .catch(() => active && setError(true));
    return () => { active = false; };
  }, [targetCode]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${target?.label || 'Language'} Quick Sheet | SublingoX`;
    return () => { document.title = previousTitle; };
  }, [target?.label]);

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-6xl">
        <section className="overflow-hidden rounded-3xl border border-indigo-100 bg-white shadow-xl shadow-indigo-950/5">
          <header className="bg-gradient-to-r from-indigo-700 to-violet-700 px-5 py-7 text-white sm:px-8">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-4">
                <LanguageFlag flag={target?.flag} label={target?.label} className="mt-1 h-9 w-12" />
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-200">{t('quickSheetPage.eyebrow')}</p>
                  <h1 className="mt-1 text-3xl font-black">{sheet?.title || t('quickSheetPage.titleFallback', { lang: target?.label || t('quickSheetPage.defaultLanguage') })}</h1>
                  <p className="mt-2 max-w-2xl text-sm text-indigo-100">{sheet?.subtitle || t('quickSheetPage.loadingSubtitle')}</p>
                </div>
              </div>
              <div className="print:hidden flex shrink-0 gap-2">
                <button type="button" onClick={openLanguagePreferences} className="rounded-xl bg-white/15 px-4 py-2.5 text-sm font-bold text-white hover:bg-white/25">{t('quickSheetPage.changeTarget')}</button>
                <button type="button" onClick={() => window.print()} className="rounded-xl bg-white px-4 py-2.5 text-sm font-black text-indigo-700 hover:bg-indigo-50">{t('quickSheetPage.printSheet')}</button>
              </div>
            </div>
          </header>
          {!sheet && !error && <div className="grid gap-4 p-5 sm:grid-cols-2 sm:p-8">{[1, 2, 3, 4].map((item) => <div key={item} className="h-40 animate-pulse rounded-2xl bg-slate-100" />)}</div>}
          {error && <p className="p-10 text-center font-semibold text-slate-600">{t('quickSheetPage.loadError')}</p>}
          {sheet && (
            <div className="p-4 sm:p-8">
              <aside className="mb-7 rounded-2xl border border-amber-200 bg-amber-50 p-4">
                <h2 className="text-sm font-black text-amber-900">{t('quickSheetPage.examReminders')}</h2>
                <ul className="mt-2 grid gap-2 text-sm text-amber-950 md:grid-cols-3">
                  {sheet.examTips.map((tip) => <li key={tip} className="flex gap-2"><span aria-hidden="true">✓</span><span>{tip}</span></li>)}
                </ul>
              </aside>
              <div className="space-y-8">
                {sheet.sections.map((section) => (
                  <article key={section.id}>
                    <h2 className="text-xl font-black text-slate-900">{section.title}</h2>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{section.note}</p>
                    <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200">
                      <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                        <thead className="bg-slate-100 text-slate-700"><tr>{section.columns.map((column) => <th key={column} scope="col" className="border-b border-slate-200 px-4 py-3 font-black">{column}</th>)}</tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {section.rows.map((row, rowIndex) => (
                            <tr key={`${section.id}-${rowIndex}`} className="odd:bg-white even:bg-slate-50/60">
                              {row.map((cell, cellIndex) => cellIndex === 0
                                ? <th key={cellIndex} scope="row" className="whitespace-nowrap px-4 py-3 font-bold text-slate-800">{cell}</th>
                                : <td key={cellIndex} className="px-4 py-3 text-slate-700">{cell}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
